/*! Dekoni Graphs — interactive earpad measurement explorer. No dependencies. */
(function (root) {
  'use strict';

  var SVGNS = 'http://www.w3.org/2000/svg';
  var MINUS = '−';

  // Dekoni's band definitions, matching the V1.8.1 workbook template: both edges inclusive,
  // Highs capped at 13.15 kHz where head-simulator results stop being dependable.
  var BANDS = [
    { id: 'sub', name: 'Sub bass', range: '20–60 Hz', lo: 20, hi: 60 },
    { id: 'bass', name: 'Bass', range: '60–250 Hz', lo: 60, hi: 250 },
    { id: 'lowmid', name: 'Low mids', range: '250–500 Hz', lo: 250, hi: 500 },
    { id: 'mid', name: 'Midrange', range: '500 Hz–2 kHz', lo: 500, hi: 2000 },
    { id: 'upmid', name: 'Upper mids', range: '2–4 kHz', lo: 2000, hi: 4000 },
    { id: 'pres', name: 'Presence', range: '4–6 kHz', lo: 4000, hi: 6000 },
    { id: 'high', name: 'Highs', range: '6\u201313 kHz', lo: 6000, hi: 13150 }
  ];
  var LEVEL_BAND = [500, 2000]; // volume-matching reference: the midrange average

  var ZOOMS = {
    full: { label: 'Full range', lo: 20, hi: 20000 },
    bass: { label: 'Bass', lo: 20, hi: 400 },
    mids: { label: 'Mids', lo: 200, hi: 4000 },
    treble: { label: 'Treble', lo: 2000, hi: 20000 }
  };

  // Categorical slots, validated as an ordered set for both surfaces.
  var SERIES = {
    light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
    dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767']
  };
  // Colour follows the pad material, so Sheepskin is the same blue on every headphone.
  // Materials that never share a product prefer the same slot; a clash falls to the next free one.
  var PAD_SLOT = { sk: 0, fnsk: 1, elvl: 2, hyb: 3, chs: 4, chl: 5, pl: 6, vg: 7, sd: 7, chb: 3, vl: 2, alp: 1, stellia: 1, mid: 6, jrz: 0, gel: 7, pu: 5 };

  var DEFAULTS = {
    data: null,            // { index: [...], headphones: { id: {...} } } to run without fetching
    indexUrl: null,        // URL of index.json
    headphoneUrl: null,    // URL pattern with {id}, e.g. "https://cdn/.../dkg-{id}.json"
    headphone: null,       // initial headphone id
    pads: null,            // initial visible pad ids (default: all)
    view: 'delta',         // 'delta' | 'spl' | 'across'
    lockHeadphone: false,  // hide the headphone picker (product pages)
    theme: 'light',        // 'light' | 'dark' | 'auto'
    shareLinks: true,
    saveImage: true,
    audio: true,
    newTabLinks: false,    // open Shop links in a new tab (for embeds outside the store)
    watermark: 'dekoniaudio.com'
  };

  /* ---------- small helpers ---------- */

  function h(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'class') n.className = attrs[k];
      else if (k.slice(0, 2) === 'on') n.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] !== null && attrs[k] !== undefined) n.setAttribute(k, attrs[k]);
    }
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }
  function s(tag, attrs, text) {
    var n = document.createElementNS(SVGNS, tag);
    if (attrs) for (var k in attrs) n.setAttribute(k, attrs[k]);
    if (text !== undefined) n.textContent = text;
    return n;
  }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }
  function fmtDb(v, signed) {
    if (!isFinite(v)) return '–';
    var a = Math.abs(v).toFixed(1);
    if (!signed) return (v < 0 ? MINUS : '') + a;
    if (a === '0.0') return '0.0';
    return (v > 0 ? '+' : MINUS) + a;
  }
  function fmtHz(f) {
    if (f >= 1000) return (f / 1000).toFixed(f >= 10000 ? 1 : 2).replace(/\.?0+$/, '') + ' kHz';
    return Math.round(f) + ' Hz';
  }
  function tickHz(f) { return f >= 1000 ? (f / 1000) + 'k' : String(f); }

  /* ---------- signal maths ---------- */

  function bandMean(freq, v, lo, hi) {
    var sum = 0, n = 0;
    for (var i = 0; i < freq.length; i++) {
      if (freq[i] >= lo && freq[i] <= hi && isFinite(v[i])) { sum += v[i]; n++; }
    }
    return n ? sum / n : NaN;
  }

  // Fractional-octave smoothing: mean of the dB values inside a 1/n-octave window.
  function smooth(freq, v, n) {
    if (!n) return v;
    var half = Math.pow(2, 1 / (2 * n)), out = new Array(v.length), lo = 0, hi = 0;
    for (var i = 0; i < v.length; i++) {
      var fl = freq[i] / half, fh = freq[i] * half;
      while (freq[lo] < fl) lo++;
      while (hi < v.length - 1 && freq[hi + 1] <= fh) hi++;
      var sum = 0, c = 0;
      for (var j = lo; j <= hi; j++) if (isFinite(v[j])) { sum += v[j]; c++; }
      out[i] = c ? sum / c : NaN;
    }
    return out;
  }

  // Resample onto another frequency grid, linear in log-frequency.
  function resample(freq, v, grid) {
    var out = new Array(grid.length), j = 0;
    for (var i = 0; i < grid.length; i++) {
      var f = grid[i];
      if (f <= freq[0]) { out[i] = v[0]; continue; }
      if (f >= freq[freq.length - 1]) { out[i] = v[v.length - 1]; continue; }
      while (freq[j + 1] < f) j++;
      var t = Math.log(f / freq[j]) / Math.log(freq[j + 1] / freq[j]);
      out[i] = v[j] + (v[j + 1] - v[j]) * t;
    }
    return out;
  }

  function nearestIndex(freq, f) {
    var lo = 0, hi = freq.length - 1;
    while (hi - lo > 1) { var m = (lo + hi) >> 1; if (freq[m] < f) lo = m; else hi = m; }
    return (f / freq[lo] < freq[hi] / f) ? lo : hi;
  }

  function clean(arr) { return arr.map(function (x) { return (x === null || x === undefined) ? NaN : x; }); }

  /* ---------- the tool ---------- */

  function Tool(mount, cfg) {
    this.cfg = {};
    for (var k in DEFAULTS) this.cfg[k] = (cfg && cfg[k] !== undefined) ? cfg[k] : DEFAULTS[k];
    this.mount = mount;
    this.cache = {};
    this.state = {
      hp: this.cfg.headphone, view: this.cfg.view, baseline: null, hidden: {},
      smooth: 0, level: 'measured', zoom: 'full', acrossPad: null, listenPad: null
    };
    this.hoverId = null;
    this.audio = null;
    this.build();
    var self = this;
    this.loadIndex().then(function () {
      self.readHash();
      if (!self.state.hp || !self.findIndexEntry(self.state.hp)) self.state.hp = self.index[0].id;
      self.fillPicker();
      return self.selectHeadphone(self.state.hp, true);
    }).catch(function (err) { self.fail(err); });
  }

  Tool.prototype.fail = function (err) {
    clear(this.chartWrap);
    this.chartWrap.appendChild(h('p', { class: 'dkg-error', text: 'The measurement data could not be loaded. Check the data URLs in the tool settings.' }));
    if (root.console) console.error('[DekoniGraphs]', err);
  };

  /* ----- data ----- */

  Tool.prototype.loadIndex = function () {
    var self = this, d = this.cfg.data;
    if (d) {
      this.index = d.index;
      for (var id in d.headphones) this.cache[id] = this.normalise(d.headphones[id]);
      return Promise.resolve();
    }
    return fetch(this.cfg.indexUrl).then(function (r) {
      if (!r.ok) throw new Error('index ' + r.status);
      return r.json();
    }).then(function (idx) { self.index = idx; });
  };

  Tool.prototype.normalise = function (hp) {
    hp.pads.forEach(function (p) { p.spl = clean(p.spl); });
    return hp;
  };

  Tool.prototype.loadHeadphone = function (id) {
    var self = this;
    if (this.cache[id]) return Promise.resolve(this.cache[id]);
    var entry = this.findIndexEntry(id), url = this.cfg.headphoneUrl.replace('{id}', id);
    if (entry && entry.v) url += (url.indexOf('?') < 0 ? '?' : '&') + 'v=' + entry.v; // cache-bust on every data change
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error(id + ' ' + r.status);
      return r.json();
    }).then(function (hp) { return (self.cache[id] = self.normalise(hp)); });
  };

  Tool.prototype.findIndexEntry = function (id) {
    return this.index.filter(function (e) { return e.id === id; })[0];
  };

  Tool.prototype.selectHeadphone = function (id, initial) {
    var self = this;
    this.root.classList.add('dkg-loading');
    return this.loadHeadphone(id).then(function (hp) {
      self.hp = hp;
      self.state.hp = id;
      self.picker.value = id;
      var st = self.state, ids = hp.pads.map(function (p) { return p.id; });
      if (ids.indexOf(st.baseline) < 0) st.baseline = hp.baseline || ids[0];
      if (!initial || !self.hashHidden) {
        st.hidden = {};
        if (initial && self.cfg.pads) ids.forEach(function (pid) {
          if (self.cfg.pads.indexOf(pid) < 0 && pid !== st.baseline) st.hidden[pid] = true;
        });
      }
      if (ids.indexOf(st.listenPad) < 0 || st.listenPad === st.baseline) {
        st.listenPad = ids.filter(function (p) { return p !== st.baseline; })[0] || null;
      }
      self.assignColours();
      self.root.classList.remove('dkg-loading');
      return self.update();
    });
  };

  Tool.prototype.assignColours = function () {
    var used = {}, slots = {}, overflow = 0;
    this.hp.pads.forEach(function (p) {
      var k = p.id.replace(/-\d+$/, '');
      if (PAD_SLOT[k] !== undefined && !used[PAD_SLOT[k]] && k === p.id) { slots[p.id] = PAD_SLOT[k]; used[PAD_SLOT[k]] = true; }
    });
    var baseId = this.hp.baseline;
    this.hp.pads.forEach(function (p) {
      if (slots[p.id] !== undefined || p.id === baseId || p.id === 'stock') return;
      for (var i = 0; i < 8; i++) if (!used[i]) { slots[p.id] = i; used[i] = true; return; }
      slots[p.id] = 8 + (overflow++ % 8);
    });
    this.slots = slots;
  };

  Tool.prototype.colourOf = function (padId) {
    var t = this.theme, slot = this.slots[padId];
    if (slot === undefined) return t.ink;      // stock / reference pad wears ink, as in Dekoni's printed graphs
    return SERIES[t.mode][slot % 8];
  };
  Tool.prototype.dashOf = function (padId) { return this.slots[padId] >= 8 ? '7 4' : null; };

  /* ----- derived series ----- */

  Tool.prototype.computePads = function () {
    var st = this.state, hp = this.hp, freq = hp.freq;
    var base = hp.pads.filter(function (p) { return p.id === st.baseline; })[0] || hp.pads[0];
    var rows = hp.pads.map(function (p) { return { id: p.id, name: p.name, url: p.url, v: smooth(freq, p.spl, st.smooth) }; });
    var baseRow = rows.filter(function (r) { return r.id === base.id; })[0];
    var ref = bandMean(freq, baseRow.v, LEVEL_BAND[0], LEVEL_BAND[1]);
    rows.forEach(function (r) {
      r.offset = bandMean(freq, r.v, LEVEL_BAND[0], LEVEL_BAND[1]) - ref;
      if (st.level === 'matched') r.v = r.v.map(function (x) { return x - r.offset; });
      r.delta = r.v.map(function (x, i) { return x - baseRow.v[i]; });
      r.isBase = r.id === base.id;
    });
    var self = this;
    rows.forEach(function (r) {
      r.y = st.view === 'spl' ? r.v : r.delta;
      r.colour = self.colourOf(r.id);
      r.dash = self.dashOf(r.id);
      r.visible = !st.hidden[r.id];
      r.width = r.isBase ? 2.25 : 2;
    });
    return { freq: freq, rows: rows, base: baseRow };
  };

  Tool.prototype.acrossOptions = function () {
    var count = {}, names = {};
    this.index.forEach(function (e) {
      e.pads.forEach(function (p) {
        if (p.id === e.baseline || /-\d+$/.test(p.id) || e.baseline !== 'stock') return;
        count[p.id] = (count[p.id] || 0) + 1; names[p.id] = p.name;
      });
    });
    return Object.keys(count).filter(function (k) { return count[k] >= 2; })
      .sort(function (a, b) { return count[b] - count[a]; })
      .map(function (k) { return { id: k, name: names[k], n: count[k] }; });
  };

  Tool.prototype.computeAcross = function () {
    var self = this, st = this.state, grid = this.hp.freq;
    var entries = this.index.filter(function (e) {
      return e.baseline === 'stock' && e.pads.some(function (p) { return p.id === st.acrossPad; });
    });
    return Promise.all(entries.map(function (e) { return self.loadHeadphone(e.id); })).then(function (hps) {
      var rows = hps.map(function (hp) {
        var pad = hp.pads.filter(function (p) { return p.id === st.acrossPad; })[0];
        var base = hp.pads.filter(function (p) { return p.id === hp.baseline; })[0];
        var d = smooth(hp.freq, pad.spl.map(function (x, i) { return x - base.spl[i]; }), st.smooth);
        var off = bandMean(hp.freq, d, LEVEL_BAND[0], LEVEL_BAND[1]);
        if (st.level === 'matched') d = d.map(function (x) { return x - off; });
        var y = resample(hp.freq, d, grid), current = hp.id === self.hp.id;
        return {
          id: hp.id, name: hp.brand + ' ' + hp.model, y: y, delta: y, offset: off, visible: true, isCurrent: current,
          colour: current ? SERIES[self.theme.mode][0] : self.theme.ghost, width: current ? 2 : 1.25
        };
      });
      var mean = grid.map(function (_, i) {
        var sum = 0, n = 0;
        rows.forEach(function (r) { if (isFinite(r.y[i])) { sum += r.y[i]; n++; } });
        return n ? sum / n : NaN;
      });
      rows.sort(function (a, b) { return (a.isCurrent ? 1 : 0) - (b.isCurrent ? 1 : 0); }); // current drawn above the rest
      rows.push({ id: '__mean', name: 'Average of ' + rows.length + ' headphones', y: mean, delta: mean, visible: true, isMean: true, colour: self.theme.ink, width: 2.75 });
      return { freq: grid, rows: rows, base: null };
    });
  };

  /* ----- DOM skeleton ----- */

  Tool.prototype.build = function () {
    var self = this, cfg = this.cfg;
    this.uid = 'dkg' + Math.random().toString(36).slice(2, 7);
    this.root = h('div', { class: 'dkg', 'data-dkg-theme': cfg.theme });

    this.picker = h('select', { id: this.uid + '-hp', class: 'dkg-select', onchange: function () { self.selectHeadphone(this.value); } });
    var pickerWrap = h('label', { class: 'dkg-field dkg-field-hp', for: this.uid + '-hp' }, [h('span', { class: 'dkg-label', text: 'Headphone' }), this.picker]);
    if (cfg.lockHeadphone) pickerWrap.hidden = true;

    this.viewSeg = this.segmented('View', [['delta', 'Change vs. baseline'], ['spl', 'Frequency response'], ['across', 'Across headphones']], 'view');
    this.actions = h('div', { class: 'dkg-actions' });
    if (cfg.shareLinks) this.actions.appendChild(h('button', { type: 'button', class: 'dkg-btn', text: 'Copy link', onclick: function () { self.copyLink(this); } }));
    if (cfg.saveImage) this.actions.appendChild(h('button', { type: 'button', class: 'dkg-btn', text: 'Save image', onclick: function () { self.saveImage(); } }));

    this.acrossSel = h('select', { id: this.uid + '-ap', class: 'dkg-select', onchange: function () { self.state.acrossPad = this.value; self.update(); } });
    this.acrossField = h('label', { class: 'dkg-field', for: this.uid + '-ap' }, [h('span', { class: 'dkg-label', text: 'Pad' }), this.acrossSel]);

    var filters = h('div', { class: 'dkg-filters' }, [
      this.acrossField,
      this.segmented('Smoothing', [[0, 'As measured'], [12, '1/12 oct'], [6, '1/6'], [3, '1/3']], 'smooth'),
      this.segmented('Level', [['measured', 'As measured'], ['matched', 'Volume-matched']], 'level'),
      this.segmented('Range', Object.keys(ZOOMS).map(function (k) { return [k, ZOOMS[k].label]; }), 'zoom')
    ]);

    this.titleEl = h('h3', { class: 'dkg-title' });
    this.subEl = h('p', { class: 'dkg-sub' });
    this.chartWrap = h('div', { class: 'dkg-chart' });
    this.tip = h('div', { class: 'dkg-tip', hidden: '' });
    this.legend = h('div', { class: 'dkg-legend' });
    this.noteEl = h('p', { class: 'dkg-note' });
    this.tableWrap = h('div', { class: 'dkg-tablewrap' });
    this.listen = h('div', { class: 'dkg-listen' });
    this.live = h('div', { class: 'dkg-sr', 'aria-live': 'polite' });

    this.root.appendChild(h('div', { class: 'dkg-top' }, [pickerWrap, this.viewSeg, this.actions]));
    this.root.appendChild(filters);
    this.root.appendChild(h('div', { class: 'dkg-main' }, [
      h('div', { class: 'dkg-plot' }, [h('div', { class: 'dkg-head' }, [this.titleEl, this.subEl]), h('div', { class: 'dkg-chartbox' }, [this.chartWrap, this.tip]), this.noteEl]),
      this.legend
    ]));
    this.root.appendChild(this.tableWrap);
    this.root.appendChild(this.listen);
    this.root.appendChild(this.live);
    this.mount.appendChild(this.root);

    if (root.ResizeObserver) {
      var last = 0;
      new ResizeObserver(function () {
        var w = self.chartWrap.clientWidth;
        if (w && Math.abs(w - last) > 1 && self.view) { last = w; self.draw(); }
      }).observe(this.chartWrap);
    }
    var redraw = function () { if (self.view) self.update(); };
    if (root.matchMedia) {
      var mq = root.matchMedia('(prefers-color-scheme: dark)');
      if (mq.addEventListener) mq.addEventListener('change', redraw);
    }
    if (root.MutationObserver) new MutationObserver(redraw).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
  };

  Tool.prototype.segmented = function (label, options, key) {
    var self = this;
    var group = h('div', { class: 'dkg-seg', role: 'group', 'aria-label': label });
    options.forEach(function (o) {
      group.appendChild(h('button', {
        type: 'button', class: 'dkg-segbtn', 'data-value': String(o[0]), text: o[1],
        onclick: function () {
          if (key === 'view' && o[0] === 'across' && !self.state.acrossPad) {
            var opts = self.acrossOptions();
            var focus = opts.filter(function (x) { return x.id === self.state.listenPad; })[0] || opts[0];
            self.state.acrossPad = focus ? focus.id : null;
          }
          self.state[key] = o[0];
          self.update();
        }
      }));
    });
    var wrap = h('div', { class: 'dkg-field' }, [h('span', { class: 'dkg-label', text: label }), group]);
    wrap.sync = function () {
      Array.prototype.forEach.call(group.children, function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === String(self.state[key])));
      });
    };
    (this.segs = this.segs || []).push(wrap);
    return wrap;
  };

  Tool.prototype.fillPicker = function () {
    var groups = {}, self = this;
    clear(this.picker);
    this.index.forEach(function (e) {
      if (!groups[e.brand]) { groups[e.brand] = h('optgroup', { label: e.brand }); self.picker.appendChild(groups[e.brand]); }
      groups[e.brand].appendChild(h('option', { value: e.id, text: e.model }));
    });
    clear(this.acrossSel);
    this.acrossOptions().forEach(function (o) {
      self.acrossSel.appendChild(h('option', { value: o.id, text: o.name + ' · ' + o.n + ' headphones' }));
    });
    if (this.acrossSel.options.length < 1) {
      var btn = this.viewSeg.querySelector('[data-value="across"]');
      if (btn) btn.hidden = true;
    }
  };

  /* ----- theme ----- */

  Tool.prototype.readTheme = function () {
    var cs = getComputedStyle(this.root);
    function v(name, fb) { return (cs.getPropertyValue(name) || '').trim() || fb; }
    this.theme = {
      mode: v('--dkg-mode', 'light') === 'dark' ? 'dark' : 'light',
      surface: v('--dkg-surface', '#fcfcfb'), ink: v('--dkg-ink', '#0b0b0b'), ink2: v('--dkg-ink-2', '#52514e'),
      muted: v('--dkg-muted', '#898781'), grid: v('--dkg-grid', '#e1e0d9'), axis: v('--dkg-axis', '#c3c2b7'),
      ghost: v('--dkg-ghost', '#b9b8b1'), brand: v('--dkg-brand', '#26587c')
    };
  };

  /* ----- update cycle ----- */

  Tool.prototype.update = function () {
    var self = this, st = this.state;
    this.readTheme();
    this.segs.forEach(function (w) { w.sync(); });
    this.acrossField.hidden = st.view !== 'across';
    if (st.view === 'across') this.acrossSel.value = st.acrossPad;
    var splBtn = this.viewSeg.querySelector('[data-value="spl"]');
    if (splBtn) {
      splBtn.hidden = !!(this.hp && this.hp.deltaOnly);
      if (splBtn.hidden && st.view === 'spl') { st.view = 'delta'; this.segs.forEach(function (w) { w.sync(); }); }
    }
    var ready = st.view === 'across' ? this.computeAcross() : Promise.resolve(this.computePads());
    var token = (this.token = (this.token || 0) + 1);
    return ready.then(function (view) {
      if (token !== self.token) return;
      self.view = view;
      self.describe();
      self.draw();
      self.renderLegend();
      self.renderTable();
      self.renderListen();
      self.writeHash();
      if (self.audio && self.audio.playing) self.audioRefresh();
    }).catch(function (err) { self.fail(err); });
  };

  Tool.prototype.baseName = function () { return this.view.base ? this.view.base.name : 'Stock'; };

  Tool.prototype.describe = function () {
    var st = this.state, hp = this.hp, name = hp.brand + ' ' + hp.model, title;
    if (st.view === 'across') {
      var opt = this.acrossOptions().filter(function (o) { return o.id === st.acrossPad; })[0];
      title = (opt ? opt.name : 'Pad') + ' vs. stock pads, across headphones';
    } else if (st.view === 'delta') title = name + ' — change vs. ' + this.baseName() + ' pads';
    else title = name + ' — frequency response by pad';
    this.titleEl.textContent = title;
    var bits = [];
    bits.push(st.smooth ? '1/' + st.smooth + '-octave smoothing' : 'as measured');
    if (st.level === 'matched') bits.push('volume-matched at 500 Hz–2 kHz');
    this.subEl.textContent = bits.join(' · ');
    this.noteEl.textContent = st.view === 'spl'
      ? (hp.unit === 'dBr' ? 'Relative level (dBr), uncompensated.' : 'dB SPL at the ear simulator, uncompensated.') + ' Shaded: above 10 kHz results depend heavily on how the headphone sits, so read that region loosely.'
      : 'Each line is that pad’s response minus the ' + (st.view === 'across' ? 'stock' : this.baseName().toLowerCase()) + ' pad’s, on the same headphone and rig. Gridlines are 1 dB. Shaded: above 10 kHz results depend heavily on how the headphone sits.';
  };

  /* ----- chart ----- */

  Tool.prototype.scales = function (W, H) {
    var st = this.state, view = this.view, z = ZOOMS[st.zoom];
    var m = { l: 46, r: 14, t: 26, b: 30 }, pw = W - m.l - m.r, ph = H - m.t - m.b;
    var lo = Infinity, hi = -Infinity;
    view.rows.forEach(function (r) {
      if (!r.visible) return;
      for (var i = 0; i < view.freq.length; i++) {
        var f = view.freq[i];
        if (f < z.lo || f > z.hi || !isFinite(r.y[i])) continue;
        if (r.y[i] < lo) lo = r.y[i];
        if (r.y[i] > hi) hi = r.y[i];
      }
    });
    if (!isFinite(lo)) { lo = -3; hi = 3; }
    var y0, y1, step, labelEvery;
    if (st.view === 'spl') {
      y0 = Math.floor((lo - 2) / 5) * 5; y1 = Math.ceil((hi + 2) / 5) * 5;
      while (y1 - y0 < 40) { y1 += 5; if (y1 - y0 < 40) y0 -= 5; }
      step = 5; labelEvery = 10;
    } else {
      y1 = Math.max(3, Math.ceil(hi / 3) * 3); y0 = Math.min(-3, Math.floor(lo / 3) * 3);
      while (y1 - y0 < 12) { y1 += 3; y0 -= 3; }
      step = 1; labelEvery = (y1 - y0) > 24 ? 6 : 3;
    }
    var lx0 = Math.log(z.lo), lx1 = Math.log(z.hi);
    return {
      m: m, pw: pw, ph: ph, W: W, H: H, f0: z.lo, f1: z.hi, y0: y0, y1: y1, step: step, labelEvery: labelEvery,
      x: function (f) { return m.l + (Math.log(f) - lx0) / (lx1 - lx0) * pw; },
      y: function (v) { return m.t + (y1 - v) / (y1 - y0) * ph; },
      fAt: function (px) { return Math.exp(lx0 + (px - m.l) / pw * (lx1 - lx0)); }
    };
  };

  // Draws the static chart into an <svg>. Shared by the live view and the image export.
  Tool.prototype.paint = function (svg, sc) {
    var t = this.theme, st = this.state, view = this.view, m = sc.m, clipId = this.uid + '-clip' + (svg === this.svg ? '' : 'x');
    var g = s('g'); svg.appendChild(g);
    var defs = s('defs'), cp = s('clipPath', { id: clipId });
    cp.appendChild(s('rect', { x: m.l, y: m.t, width: sc.pw, height: sc.ph }));
    defs.appendChild(cp); g.appendChild(defs);

    // placement-sensitive region
    if (sc.f1 > 10000) g.appendChild(s('rect', { x: sc.x(10000), y: m.t, width: sc.x(sc.f1) - sc.x(10000), height: sc.ph, fill: t.ink, 'fill-opacity': 0.035 }));

    // frequency grid
    var narrow = sc.pw < 430, decades = [10, 100, 1000, 10000];
    var labelled = narrow ? [20, 100, 1000, 10000] : [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
    decades.forEach(function (d) {
      for (var k = 1; k < 10; k++) {
        var f = d * k;
        if (f < sc.f0 || f > sc.f1) continue;
        g.appendChild(s('line', { x1: sc.x(f), x2: sc.x(f), y1: m.t, y2: m.t + sc.ph, stroke: t.grid, 'stroke-width': 1, 'stroke-opacity': k === 1 ? 1 : 0.55 }));
      }
    });
    labelled.concat([sc.f0, sc.f1]).filter(function (f, i, a) { return a.indexOf(f) === i && f >= sc.f0 && f <= sc.f1; }).forEach(function (f) {
      var edge = f === sc.f0 ? 'start' : (f === sc.f1 ? 'end' : 'middle');
      var near = labelled.some(function (l) { return l !== f && (f === sc.f0 || f === sc.f1) && Math.abs(sc.x(l) - sc.x(f)) < 30 && l > sc.f0 && l < sc.f1; });
      if (near) return;
      g.appendChild(s('text', { x: sc.x(f), y: m.t + sc.ph + 18, 'text-anchor': edge, fill: t.muted, 'font-size': 11, class: 'dkg-num' }, tickHz(f)));
    });
    g.appendChild(s('text', { x: m.l + sc.pw, y: sc.H - 1, 'text-anchor': 'end', fill: t.muted, 'font-size': 10 }, 'Hz'));

    // level grid
    for (var v = sc.y0; v <= sc.y1 + 1e-9; v += sc.step) {
      var major = Math.round(v) % sc.labelEvery === 0, zero = st.view !== 'spl' && Math.round(v) === 0;
      g.appendChild(s('line', { x1: m.l, x2: m.l + sc.pw, y1: sc.y(v), y2: sc.y(v), stroke: zero ? t.axis : t.grid, 'stroke-width': zero ? 1.5 : 1, 'stroke-opacity': (major || zero) ? 1 : 0.55 }));
      if (major) g.appendChild(s('text', { x: m.l - 8, y: sc.y(v) + 4, 'text-anchor': 'end', fill: t.muted, 'font-size': 11, class: 'dkg-num' }, st.view === 'spl' ? String(v) : fmtDb(v, true).replace('.0', '')));
    }
    g.appendChild(s('text', { x: 2, y: m.t - 10, fill: t.muted, 'font-size': 10 }, st.view === 'spl' ? (this.hp.unit || 'dB SPL') : 'dB vs. baseline'));

    // band guide along the top edge
    BANDS.forEach(function (b) {
      var lo = Math.max(b.lo, sc.f0), hi = Math.min(b.hi, sc.f1);
      if (hi <= lo) return;
      var x0 = sc.x(lo), x1 = sc.x(hi);
      if (b.lo > sc.f0) g.appendChild(s('line', { x1: x0, x2: x0, y1: m.t - 6, y2: m.t + 6, stroke: t.axis, 'stroke-width': 1 }));
      if (x1 - x0 > b.name.length * 6.2 + 8) g.appendChild(s('text', { x: (x0 + x1) / 2, y: m.t - 9, 'text-anchor': 'middle', fill: t.ink2, 'font-size': 10.5, 'letter-spacing': '.02em' }, b.name));
    });

    // traces
    var paths = {}, lines = s('g', { 'clip-path': 'url(#' + clipId + ')' });
    view.rows.forEach(function (r) {
      if (!r.visible) return;
      var d = '', pen = false;
      for (var i = 0; i < view.freq.length; i++) {
        var f = view.freq[i];
        if (!isFinite(r.y[i]) || f < sc.f0 * 0.98 || f > sc.f1 * 1.02) { pen = false; continue; }
        d += (pen ? 'L' : 'M') + sc.x(f).toFixed(1) + ' ' + sc.y(r.y[i]).toFixed(1);
        pen = true;
      }
      var p = s('path', { d: d, fill: 'none', stroke: r.colour, 'stroke-width': r.width, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' });
      if (r.dash) p.setAttribute('stroke-dasharray', r.dash);
      if (r.isBase && st.view === 'delta') p.setAttribute('stroke-opacity', 0); // the baseline is the zero line
      lines.appendChild(p); paths[r.id] = p;
    });
    g.appendChild(lines);
    if (st.view === 'delta' && view.base) g.appendChild(s('text', { x: m.l + 6, y: sc.y(0) - 5, fill: t.ink2, 'font-size': 10.5 }, view.base.name + ' = 0 dB'));
    return paths;
  };

  Tool.prototype.draw = function () {
    var self = this, W = Math.max(280, this.chartWrap.clientWidth || 640);
    var H = Math.round(Math.max(260, Math.min(500, W * 0.52)));
    var sc = (this.sc = this.scales(W, H));
    clear(this.chartWrap);
    var svg = (this.svg = s('svg', { viewBox: '0 0 ' + W + ' ' + H, width: W, height: H, role: 'img', tabindex: 0, 'aria-label': this.titleEl.textContent + '. Use left and right arrow keys to read values.' }));
    this.paths = this.paint(svg, sc);
    this.cross = s('g', { visibility: 'hidden' });
    svg.appendChild(this.cross);
    var hit = s('rect', { x: sc.m.l, y: sc.m.t, width: sc.pw, height: sc.ph, fill: 'transparent', style: 'touch-action:pan-y' });
    svg.appendChild(hit);
    hit.addEventListener('pointermove', function (e) {
      var box = svg.getBoundingClientRect();
      self.probe(nearestIndex(self.view.freq, sc.fAt((e.clientX - box.left) * (W / box.width))));
    });
    hit.addEventListener('pointerleave', function () { self.probe(-1); });
    svg.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Escape') return;
      e.preventDefault();
      if (e.key === 'Escape') return self.probe(-1);
      var i = self.probeIdx >= 0 ? self.probeIdx : nearestIndex(self.view.freq, 1000);
      i += (e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 12 : 3);
      self.probe(Math.max(0, Math.min(self.view.freq.length - 1, i)), true);
    });
    svg.addEventListener('blur', function () { self.probe(-1); });
    this.chartWrap.appendChild(svg);
    this.applyHover();
  };

  Tool.prototype.probe = function (idx, announce) {
    var sc = this.sc, view = this.view, t = this.theme, st = this.state;
    this.probeIdx = idx;
    clear(this.cross);
    if (idx < 0 || view.freq[idx] < sc.f0 || view.freq[idx] > sc.f1) { this.cross.setAttribute('visibility', 'hidden'); this.tip.hidden = true; return; }
    var f = view.freq[idx], x = sc.x(f), self = this;
    this.cross.setAttribute('visibility', 'visible');
    this.cross.appendChild(s('line', { x1: x, x2: x, y1: sc.m.t, y2: sc.m.t + sc.ph, stroke: t.brand, 'stroke-width': 1 }));
    var rows = view.rows.filter(function (r) { return r.visible && isFinite(r.y[idx]) && !(r.isBase && st.view === 'delta'); });
    rows.forEach(function (r) {
      if (st.view === 'across' && !r.isMean && !r.isCurrent && self.hoverId !== r.id) return;
      var cy = Math.max(sc.m.t, Math.min(sc.m.t + sc.ph, sc.y(r.y[idx])));
      self.cross.appendChild(s('circle', { cx: x, cy: cy, r: 4.5, fill: r.colour, stroke: t.surface, 'stroke-width': 2 }));
    });

    clear(this.tip);
    this.tip.appendChild(h('div', { class: 'dkg-tip-f', text: fmtHz(f) }));
    var list = rows.slice();
    if (st.view === 'across') {
      var others = rows.filter(function (r) { return !r.isMean; }).map(function (r) { return r.y[idx]; });
      list = rows.filter(function (r) { return r.isMean || r.isCurrent || r.id === self.hoverId; }).reverse();
      this.tipRow(null, 'Spread', fmtDb(Math.min.apply(null, others), true) + ' to ' + fmtDb(Math.max.apply(null, others), true) + ' dB', true);
    } else list.sort(function (a, b) { return b.y[idx] - a.y[idx]; });
    list.forEach(function (r) { self.tipRow(r.colour, r.name, fmtDb(r.y[idx], st.view !== 'spl') + ' dB'); });
    if (st.view === 'delta') this.tip.appendChild(h('div', { class: 'dkg-tip-foot', text: 'relative to ' + this.baseName() }));
    this.tip.hidden = false;
    var cw = this.chartWrap.clientWidth, px = x * (cw / sc.W), tw = this.tip.offsetWidth;
    this.tip.style.left = Math.round(px + 14 + tw > cw ? Math.max(0, px - 14 - tw) : px + 14) + 'px';
    this.tip.style.top = (sc.m.t + 6) + 'px';
    if (announce) this.live.textContent = fmtHz(f) + ': ' + list.map(function (r) { return r.name + ' ' + fmtDb(r.y[idx], st.view !== 'spl') + ' dB'; }).join(', ');
  };

  Tool.prototype.tipRow = function (colour, name, value, plain) {
    var key = h('span', { class: 'dkg-key' });
    if (colour) key.style.background = colour; else key.style.visibility = 'hidden';
    this.tip.appendChild(h('div', { class: 'dkg-tip-row' + (plain ? ' dkg-tip-plain' : '') }, [key, h('span', { class: 'dkg-tip-v dkg-num', text: value }), h('span', { class: 'dkg-tip-n', text: name })]));
  };

  Tool.prototype.setHover = function (id) { this.hoverId = id; this.applyHover(); };
  Tool.prototype.applyHover = function () {
    var id = this.hoverId, st = this.state, paths = this.paths || {};
    for (var k in paths) {
      var isBaseZero = st.view === 'delta' && this.view.base && k === this.view.base.id;
      if (isBaseZero) continue;
      paths[k].setAttribute('stroke-opacity', !id || id === k || k === '__mean' ? 1 : 0.18);
      if (st.view === 'across' && k !== '__mean') {
        var row = this.view.rows.filter(function (r) { return r.id === k; })[0];
        paths[k].setAttribute('stroke', id === k && !row.isCurrent ? SERIES[this.theme.mode][1] : row.colour);
        paths[k].setAttribute('stroke-width', id === k ? 2.25 : row.width);
      }
    }
  };

  /* ----- legend ----- */

  Tool.prototype.renderLegend = function () {
    var self = this, st = this.state, view = this.view;
    clear(this.legend);
    var across = st.view === 'across';
    this.legend.appendChild(h('div', { class: 'dkg-legend-h', text: across ? 'Headphones' : 'Pads' }));
    var rows = across ? view.rows.slice().reverse() : view.rows;
    rows.forEach(function (r) {
      var key = h('span', { class: 'dkg-key dkg-key-lg' }); key.style.background = r.colour;
      var name = h('span', { class: 'dkg-pad-name', text: r.name });
      var meta = h('span', { class: 'dkg-pad-meta' });
      var row;
      if (across) {
        row = h('div', { class: 'dkg-pad' }, [key, h('span', { class: 'dkg-pad-text' }, [name, meta])]);
        if (r.isCurrent) meta.textContent = 'selected headphone';
        if (!r.isMean && !r.isCurrent) {
          row.appendChild(h('button', { type: 'button', class: 'dkg-link', text: 'Open', 'aria-label': 'Open ' + r.name, onclick: function () { st.view = 'delta'; self.selectHeadphone(r.id); } }));
        }
      } else {
        var id = self.uid + '-p-' + r.id;
        var box = h('input', { type: 'checkbox', id: id, class: 'dkg-check' });
        box.checked = r.visible;
        box.disabled = r.isBase;
        box.addEventListener('change', function () { if (box.checked) delete st.hidden[r.id]; else st.hidden[r.id] = true; self.update(); });
        if (r.isBase) meta.textContent = 'baseline';
        else if (st.level === 'measured' && isFinite(r.offset)) meta.textContent = fmtDb(r.offset, true) + ' dB overall level';
        else if (isFinite(r.offset)) meta.textContent = 'level-matched (' + fmtDb(-r.offset, true) + ' dB)';
        row = h('div', { class: 'dkg-pad' + (r.visible ? '' : ' dkg-off') }, [box, h('label', { class: 'dkg-pad-main', for: id }, [key, h('span', { class: 'dkg-pad-text' }, [name, meta])])]);
        var tools = h('span', { class: 'dkg-pad-tools' });
        if (!r.isBase) tools.appendChild(h('button', { type: 'button', class: 'dkg-link', text: 'Set baseline', 'aria-label': 'Use ' + r.name + ' as the baseline', onclick: function () { st.baseline = r.id; delete st.hidden[r.id]; if (st.listenPad === r.id) st.listenPad = null; self.update(); } }));
        if (r.url) tools.appendChild(h('a', { class: 'dkg-link dkg-shop', href: r.url, text: 'Shop this pad', target: self.cfg.newTabLinks ? '_blank' : null, rel: self.cfg.newTabLinks ? 'noopener' : null }));
        row.appendChild(tools);
      }
      row.addEventListener('pointerenter', function () { self.setHover(r.id); });
      row.addEventListener('pointerleave', function () { self.setHover(null); });
      self.legend.appendChild(row);
    });
  };

  /* ----- band table (also the accessible table view) ----- */

  Tool.prototype.renderTable = function () {
    var self = this, st = this.state, view = this.view, t = this.theme;
    clear(this.tableWrap);
    var rows = view.rows.filter(function (r) { return r.visible && !r.isBase; });
    if (st.view === 'across') rows = rows.slice().reverse();
    if (!rows.length) return;
    var cap = st.view === 'across' ? 'Average change vs. stock pads, by band' : 'Average change vs. ' + this.baseName() + ' pads, by band';
    var table = h('table', { class: 'dkg-table' });
    table.appendChild(h('caption', { text: cap + ' (dB)' }));
    var head = h('tr', null, [h('th', { scope: 'col', text: st.view === 'across' ? 'Headphone' : 'Pad' })]);
    BANDS.forEach(function (b) { head.appendChild(h('th', { scope: 'col' }, [h('span', { text: b.name }), h('small', { text: b.range })])); });
    table.appendChild(h('thead', null, [head]));
    var body = h('tbody');
    var pole = t.mode === 'dark' ? { pos: '230,103,103', neg: '91,190,231' } : { pos: '227,73,72', neg: '38,88,124' };
    rows.forEach(function (r) {
      var key = h('span', { class: 'dkg-key' }); key.style.background = r.colour;
      var tr = h('tr', { class: r.isMean ? 'dkg-row-mean' : '' }, [h('th', { scope: 'row' }, [key, h('span', { text: r.name })])]);
      BANDS.forEach(function (b) {
        var v = bandMean(view.freq, r.delta, b.lo, b.hi), a = Math.min(1, Math.abs(v) / 6);
        var td = h('td', { class: 'dkg-num', text: fmtDb(v, true) });
        if (isFinite(v) && Math.abs(v) >= 0.05) td.style.background = 'rgba(' + (v > 0 ? pole.pos : pole.neg) + ',' + (0.06 + a * 0.44).toFixed(2) + ')';
        tr.appendChild(td);
      });
      tr.addEventListener('pointerenter', function () { self.setHover(r.id); });
      tr.addEventListener('pointerleave', function () { self.setHover(null); });
      body.appendChild(tr);
    });
    table.appendChild(body);
    this.tableWrap.appendChild(table);
    this.tableWrap.appendChild(h('p', { class: 'dkg-note', text: 'Red cells are louder than the baseline in that band, blue cells quieter; full colour at ±6 dB. Differences under about 1 dB are hard to hear.' }));
  };

  /* ----- listen: the measured difference applied as an EQ ----- */

  Tool.prototype.renderListen = function () {
    var self = this, st = this.state;
    clear(this.listen);
    var Ctx = root.AudioContext || root.webkitAudioContext;
    var pads = st.view === 'across' ? [] : this.view.rows.filter(function (r) { return !r.isBase; });
    if (!this.cfg.audio || !Ctx || !pads.length) { this.audioStop(); return; }
    if (!st.listenPad) st.listenPad = pads[0].id;
    var a = this.audio || (this.audio = { playing: false, wet: true, source: 'pink' });

    var sel = h('select', { id: this.uid + '-lp', class: 'dkg-select', onchange: function () { st.listenPad = this.value; if (a.playing) self.audioRefresh(); self.renderListen(); } });
    pads.forEach(function (r) { sel.appendChild(h('option', { value: r.id, text: r.name })); });
    sel.value = st.listenPad;
    var padName = (pads.filter(function (r) { return r.id === st.listenPad; })[0] || pads[0]).name;

    var play = h('button', { type: 'button', class: 'dkg-btn dkg-btn-solid', text: a.playing ? 'Stop' : 'Play pink noise', onclick: function () { if (a.playing) self.audioStop(); else self.audioStart(); self.renderListen(); } });
    var file = h('input', { type: 'file', accept: 'audio/*', id: this.uid + '-file', class: 'dkg-sr', onchange: function () { if (this.files[0]) self.audioLoadFile(this.files[0]); } });
    var fileBtn = h('label', { class: 'dkg-btn', for: this.uid + '-file', text: a.fileName ? 'Track: ' + a.fileName : 'Use your own track…' });

    var ab = h('div', { class: 'dkg-seg', role: 'group', 'aria-label': 'Which pad to hear' });
    [[false, this.baseName()], [true, padName]].forEach(function (o) {
      ab.appendChild(h('button', { type: 'button', class: 'dkg-segbtn', 'aria-pressed': String(a.wet === o[0]), text: o[1], onclick: function () { a.wet = o[0]; self.audioAB(); self.renderListen(); } }));
    });

    this.listen.appendChild(h('div', { class: 'dkg-listen-h' }, [h('h4', { text: 'Hear the difference' }), h('span', { class: 'dkg-badge', text: 'approximation' })]));
    this.listen.appendChild(h('div', { class: 'dkg-listen-row' }, [
      play, fileBtn, file,
      h('label', { class: 'dkg-field', for: this.uid + '-lp' }, [h('span', { class: 'dkg-label', text: 'Compare' }), sel]),
      h('div', { class: 'dkg-field' }, [h('span', { class: 'dkg-label', text: 'Now hearing' }), ab])
    ]));
    this.listen.appendChild(h('p', { class: 'dkg-note', text: 'Applies the measured difference between the two pads as an EQ, using the smoothing and level settings above. It is closest to reality when you listen on the ' + this.hp.brand + ' ' + this.hp.model + ' with ' + this.baseName().toLowerCase() + ' pads; it cannot reproduce changes in comfort, isolation or soundstage.' }));
  };

  Tool.prototype.currentDelta = function () {
    var id = this.state.listenPad;
    return this.view.rows.filter(function (r) { return r.id === id; })[0];
  };

  // Linear-phase FIR from the delta curve (frequency sampling + Hann window).
  Tool.prototype.buildIR = function (ctx, deltaDb) {
    var N = 4096, half = N / 2, sr = ctx.sampleRate, freq = this.view.freq, mag = new Float64Array(half + 1), j = 0;
    for (var k = 0; k <= half; k++) {
      var f = k * sr / N, d;
      if (f <= freq[0]) d = deltaDb[0];
      else if (f >= freq[freq.length - 1]) d = deltaDb[deltaDb.length - 1];
      else { while (freq[j + 1] < f) j++; d = deltaDb[j] + (deltaDb[j + 1] - deltaDb[j]) * Math.log(f / freq[j]) / Math.log(freq[j + 1] / freq[j]); }
      mag[k] = Math.pow(10, (isFinite(d) ? d : 0) / 20);
    }
    var cosT = new Float64Array(N);
    for (var i = 0; i < N; i++) cosT[i] = Math.cos(2 * Math.PI * i / N);
    var buf = ctx.createBuffer(1, N, sr), out = buf.getChannelData(0);
    for (var n = 0; n < N; n++) {
      var mIdx = ((n - half) % N + N) % N, acc = mag[0] + mag[half] * cosT[(half * mIdx) % N];
      for (var q = 1; q < half; q++) acc += 2 * mag[q] * cosT[(q * mIdx) % N];
      out[n] = acc / N * (0.5 - 0.5 * Math.cos(2 * Math.PI * n / (N - 1)));
    }
    return buf;
  };

  Tool.prototype.audioStart = function () {
    var a = this.audio, Ctx = root.AudioContext || root.webkitAudioContext;
    if (!a.ctx) {
      a.ctx = new Ctx();
      var len = a.ctx.sampleRate * 6, pink = a.ctx.createBuffer(2, len, a.ctx.sampleRate);
      for (var c = 0; c < 2; c++) { // Paul Kellet's pink-noise filter
        var d = pink.getChannelData(c), b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
        for (var i = 0; i < len; i++) {
          var w = Math.random() * 2 - 1;
          b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759; b2 = 0.969 * b2 + w * 0.153852;
          b3 = 0.8665 * b3 + w * 0.3104856; b4 = 0.55 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.016898;
          d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.06; b6 = w * 0.115926;
        }
      }
      a.pink = pink;
      var delay = a.ctx.createBuffer(1, 4096, a.ctx.sampleRate); delay.getChannelData(0)[2048] = 1; // dry path, same latency
      a.dryBuf = delay;
    }
    if (a.ctx.state === 'suspended') a.ctx.resume();
    this.audioStop(true);
    var ctx = a.ctx;
    a.src = ctx.createBufferSource(); a.src.buffer = a.source === 'file' && a.fileBuf ? a.fileBuf : a.pink; a.src.loop = true;
    a.dry = ctx.createConvolver(); a.dry.normalize = false; a.dry.buffer = a.dryBuf;
    a.dryGain = ctx.createGain(); a.wetGain = ctx.createGain(); a.master = ctx.createGain();
    a.src.connect(a.dry); a.dry.connect(a.dryGain); a.dryGain.connect(a.master); a.wetGain.connect(a.master); a.master.connect(ctx.destination);
    a.playing = true;
    this.audioRefresh();
    a.src.start();
  };

  Tool.prototype.audioRefresh = function () {
    var a = this.audio, row = this.currentDelta();
    if (!a || !a.playing || !row) return;
    var peak = 0;
    row.delta.forEach(function (v) { if (isFinite(v) && v > peak) peak = v; });
    if (a.conv) { try { a.src.disconnect(a.conv); a.conv.disconnect(); } catch (e) {} }
    a.conv = a.ctx.createConvolver(); a.conv.normalize = false; a.conv.buffer = this.buildIR(a.ctx, row.delta);
    a.src.connect(a.conv); a.conv.connect(a.wetGain);
    a.master.gain.value = Math.pow(10, -(peak + 1) / 20); // headroom so a boost never clips
    this.audioAB();
  };

  Tool.prototype.audioAB = function () {
    var a = this.audio;
    if (!a || !a.playing) return;
    var now = a.ctx.currentTime;
    a.wetGain.gain.setTargetAtTime(a.wet ? 1 : 0, now, 0.012);
    a.dryGain.gain.setTargetAtTime(a.wet ? 0 : 1, now, 0.012);
  };

  Tool.prototype.audioStop = function (keepFlag) {
    var a = this.audio;
    if (!a || !a.src) { if (a && !keepFlag) a.playing = false; return; }
    try { a.src.stop(); a.src.disconnect(); a.master.disconnect(); } catch (e) {}
    a.src = a.conv = null;
    if (!keepFlag) a.playing = false;
  };

  Tool.prototype.audioLoadFile = function (file) {
    var self = this, a = this.audio;
    if (!a.ctx) { this.audioStart(); this.audioStop(); }
    var reader = new FileReader();
    reader.onload = function () {
      a.ctx.decodeAudioData(reader.result, function (buf) {
        a.fileBuf = buf; a.fileName = file.name.length > 24 ? file.name.slice(0, 22) + '…' : file.name; a.source = 'file';
        self.audioStart(); self.renderListen();
      }, function () { self.live.textContent = 'That file could not be decoded. Try an MP3, WAV or FLAC.'; });
    };
    reader.readAsArrayBuffer(file);
  };

  /* ----- sharing ----- */

  Tool.prototype.writeHash = function () {
    if (!this.cfg.shareLinks) return;
    var st = this.state, q = ['hp=' + st.hp, 'view=' + st.view, 'base=' + st.baseline, 'smooth=' + st.smooth, 'level=' + st.level, 'zoom=' + st.zoom];
    var hidden = Object.keys(st.hidden);
    if (hidden.length) q.push('hide=' + hidden.join(','));
    if (st.view === 'across') q.push('pad=' + st.acrossPad);
    try { history.replaceState(null, '', '#dkg:' + q.join('&')); } catch (e) {}
  };

  Tool.prototype.readHash = function () {
    if (!this.cfg.shareLinks) return;
    var m = /^#dkg:(.*)$/.exec(location.hash || ''), st = this.state;
    if (!m) return;
    m[1].split('&').forEach(function (kv) {
      var p = kv.split('='), k = p[0], v = decodeURIComponent(p[1] || '');
      if (k === 'hp') st.hp = v;
      else if (k === 'view' && /^(delta|spl|across)$/.test(v)) st.view = v;
      else if (k === 'base') st.baseline = v;
      else if (k === 'smooth' && /^(0|3|6|12)$/.test(v)) st.smooth = +v;
      else if (k === 'level' && /^(measured|matched)$/.test(v)) st.level = v;
      else if (k === 'zoom' && ZOOMS[v]) st.zoom = v;
      else if (k === 'pad') st.acrossPad = v;
      else if (k === 'hide') v.split(',').forEach(function (id) { st.hidden[id] = true; });
    });
    this.hashHidden = true;
  };

  Tool.prototype.copyLink = function (btn) {
    var done = function (ok) { var t = btn.textContent; btn.textContent = ok ? 'Link copied' : 'Copy the address bar'; setTimeout(function () { btn.textContent = t; }, 1800); };
    try { navigator.clipboard.writeText(location.href).then(function () { done(true); }, function () { done(false); }); } catch (e) { done(false); }
  };

  Tool.prototype.saveImage = function () {
    var t = this.theme, W = 1200, H = 620, head = 64, foot = 58, sc = this.scales(W, H);
    var svg = s('svg', { xmlns: SVGNS, viewBox: '0 0 ' + W + ' ' + (H + head + foot), width: W * 2, height: (H + head + foot) * 2, 'font-family': 'Jost, Helvetica, Arial, sans-serif' });
    svg.appendChild(s('rect', { width: W, height: H + head + foot, fill: t.surface }));
    svg.appendChild(s('text', { x: 24, y: 32, fill: t.ink, 'font-size': 20, 'font-weight': 700 }, this.titleEl.textContent));
    svg.appendChild(s('text', { x: 24, y: 52, fill: t.ink2, 'font-size': 12 }, this.subEl.textContent));
    var inner = s('svg', { x: 0, y: head, width: W, height: H, viewBox: '0 0 ' + W + ' ' + H });
    this.paint(inner, sc); svg.appendChild(inner);
    var x = 46, y = H + head + 24;
    this.view.rows.filter(function (r) { return r.visible; }).slice(-9).forEach(function (r) {
      svg.appendChild(s('line', { x1: x, x2: x + 22, y1: y - 4, y2: y - 4, stroke: r.colour, 'stroke-width': 3, 'stroke-linecap': 'round' }));
      svg.appendChild(s('text', { x: x + 28, y: y, fill: t.ink, 'font-size': 13 }, r.name));
      x += 44 + r.name.length * 7.4;
      if (x > W - 220) { x = 46; y += 20; }
    });
    svg.appendChild(s('text', { x: W - 24, y: H + head + foot - 14, 'text-anchor': 'end', fill: t.brand, 'font-size': 14, 'font-weight': 600 }, this.cfg.watermark));
    var xml = new XMLSerializer().serializeToString(svg), img = new Image(), name = this.titleEl.textContent.replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '.png';
    img.onload = function () {
      var c = document.createElement('canvas'); c.width = W * 2; c.height = (H + head + foot) * 2;
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(function (blob) {
        var link = h('a', { href: URL.createObjectURL(blob), download: name });
        document.body.appendChild(link); link.click(); link.remove();
      });
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
  };

  root.DekoniGraphs = {
    mount: function (target, cfg) {
      var node = typeof target === 'string' ? document.querySelector(target) : target;
      return node ? new Tool(node, cfg) : null;
    },
    _math: { bandMean: bandMean, smooth: smooth, resample: resample, BANDS: BANDS }
  };
})(typeof window !== 'undefined' ? window : this);
